<template>
  <input
    ref="input"
    v-model="model"
    v-bind="bindings"
    :class="{ prefix: props.hasPrefix }"
    @keydown="emit('keydown', $event)"
    @focus="emit('focus', $event)"
    @blur="emit('blur', $event)"
    @beforeinput="emit('beforeinput', $event as HTMLInputEvent)"
  />
</template>

<script setup lang="ts">
import { defineInputBindings, defineInputFieldPropsDefaults } from '@/mixins/InputFieldAttributes';
import type { InputFieldProps } from '@/mixins/InputFieldAttributes';
import type { HTMLInputEvent } from '@/types/forms';

type InputProps = InputFieldProps & {
  hasPrefix?: boolean;
};

const emit = defineEmits<{
  (name: 'keydown', value: KeyboardEvent): void;
  (name: 'focus', value: FocusEvent): void;
  (name: 'blur', value: FocusEvent): void;
  (name: 'beforeinput', value: HTMLInputEvent): void;
}>();

const props = withDefaults(defineProps<InputProps>(), defineInputFieldPropsDefaults());
const model = defineModel<string>('value');
const bindings = defineInputBindings(props);
</script>

<style lang="scss" scoped>
.phone-input:not(.focused) input::placeholder {
  color: transparent;
}

input {
  @include input-element;

  &.disabled,
  &:disabled,
  &:disabled[disabled] {
    background-color: palette(grey, light);
    opacity: 0.65;
    cursor: default;
  }

  &:read-only {
    cursor: default;
  }

  // Removes the 'X' from chrome
  &::-webkit-search-decoration,
  &::-webkit-search-cancel-button,
  &::-webkit-search-results-button,
  &::-webkit-search-results-decoration {
    display: none;
  }

  // Removes date and number control arrows
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button,
  &::-webkit-calendar-picker-indicator,
  &::-webkit-clear-button {
    display: none;
    -webkit-appearance: none;
  }

  &.prefix {
    padding: 0 0 0 $pad * 3;
    line-height: 40px;
  }
}
</style>
